import * as THREE from "three";
import type { IBoundingboxViewer3dItem, RoomSectionObb } from "../RoomSectionViews/types";

type RenderObb = {
  item: IBoundingboxViewer3dItem["item"];
  obb: RoomSectionObb;
  color: string;
  opacity: number;
  depthOffsetUnits: number;
  outline?: IBoundingboxViewer3dItem["outline"];
};

const INSTANCE_CHUNK_SIZE_M = 20;
// Bound the number of additional instance batches rather than allocating a material per item.
const DEPTH_PRIORITY_BUCKETS = 16;

function getDepthOffsetUnits(item: RenderObb["item"], obb: RoomSectionObb): number {
  const identity = String(item._id ?? item.globalId ?? getDeduplicationKey(obb));
  let hash = 2166136261;
  for (let i = 0; i < identity.length; i++) {
    hash = Math.imul(hash ^ identity.charCodeAt(i), 16777619);
  }
  // Positive offsets keep outlines in front. No model transforms or picking geometry change.
  return 1 + ((hash >>> 0) % DEPTH_PRIORITY_BUCKETS) * 2;
}

function getDeduplicationKey(obb: RoomSectionObb) {
  return (
    `${Math.round(obb.centerM.x * 1000)},${Math.round(obb.centerM.y * 1000)},${Math.round(obb.centerM.z * 1000)}|` +
    `${Math.round(obb.extentAM * 1000)},${Math.round(obb.extentBM * 1000)},${Math.round(obb.extentCM * 1000)}|` +
    `${obb.axisA.x.toFixed(3)},${obb.axisA.y.toFixed(3)},${obb.axisA.z.toFixed(3)}`
  );
}

function getMaterialKey({ color, opacity }: RenderObb) {
  return `${color}|${opacity}`;
}

function getChunkKey(obb: RoomSectionObb) {
  const { centerM: c } = obb;
  return `${Math.floor(c.x / INSTANCE_CHUNK_SIZE_M)},${Math.floor(c.y / INSTANCE_CHUNK_SIZE_M)},${Math.floor(c.z / INSTANCE_CHUNK_SIZE_M)}`;
}

function getMeshGroupKey(renderObb: RenderObb) {
  return `${getMaterialKey(renderObb)}|${getChunkKey(renderObb.obb)}|${renderObb.depthOffsetUnits}`;
}

function getOutlineKey(outline: NonNullable<IBoundingboxViewer3dItem["outline"]>) {
  return `${outline.color}|${outline.opacity ?? 1}|${outline.renderOnTop === true}`;
}

function getOutlineGroupKey(outline: NonNullable<IBoundingboxViewer3dItem["outline"]>, obb: RoomSectionObb) {
  return `${getOutlineKey(outline)}|${getChunkKey(obb)}`;
}

function setObbMatrix(obb: RoomSectionObb, matrix: THREE.Matrix4, origin: THREE.Vector3) {
  const { centerM: c, axisA, axisB, axisC, extentAM: eA, extentBM: eB, extentCM: eC } = obb;
  matrix.set(
    axisA.x * 2 * eA,
    axisB.x * 2 * eB,
    axisC.x * 2 * eC,
    c.x - origin.x,
    axisA.y * 2 * eA,
    axisB.y * 2 * eB,
    axisC.y * 2 * eC,
    c.y - origin.y,
    axisA.z * 2 * eA,
    axisB.z * 2 * eB,
    axisC.z * 2 * eC,
    c.z - origin.z,
    0,
    0,
    0,
    1,
  );
}

const BOX_EDGE_INDEX_PAIRS = [
  [0, 1],
  [1, 3],
  [3, 2],
  [2, 0],
  [4, 5],
  [5, 7],
  [7, 6],
  [6, 4],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
] as const;

function addObbEdgePositions(obb: RoomSectionObb, positions: number[], origin: THREE.Vector3) {
  const { centerM: c, axisA, axisB, axisC, extentAM: eA, extentBM: eB, extentCM: eC } = obb;
  const corners = [
    [c.x - origin.x - axisA.x * eA - axisB.x * eB - axisC.x * eC, c.y - origin.y - axisA.y * eA - axisB.y * eB - axisC.y * eC, c.z - origin.z - axisA.z * eA - axisB.z * eB - axisC.z * eC],
    [c.x - origin.x + axisA.x * eA - axisB.x * eB - axisC.x * eC, c.y - origin.y + axisA.y * eA - axisB.y * eB - axisC.y * eC, c.z - origin.z + axisA.z * eA - axisB.z * eB - axisC.z * eC],
    [c.x - origin.x - axisA.x * eA + axisB.x * eB - axisC.x * eC, c.y - origin.y - axisA.y * eA + axisB.y * eB - axisC.y * eC, c.z - origin.z - axisA.z * eA + axisB.z * eB - axisC.z * eC],
    [c.x - origin.x + axisA.x * eA + axisB.x * eB - axisC.x * eC, c.y - origin.y + axisA.y * eA + axisB.y * eB - axisC.y * eC, c.z - origin.z + axisA.z * eA + axisB.z * eB - axisC.z * eC],
    [c.x - origin.x - axisA.x * eA - axisB.x * eB + axisC.x * eC, c.y - origin.y - axisA.y * eA - axisB.y * eB + axisC.y * eC, c.z - origin.z - axisA.z * eA - axisB.z * eB + axisC.z * eC],
    [c.x - origin.x + axisA.x * eA - axisB.x * eB + axisC.x * eC, c.y - origin.y + axisA.y * eA - axisB.y * eB + axisC.y * eC, c.z - origin.z + axisA.z * eA - axisB.z * eB + axisC.z * eC],
    [c.x - origin.x - axisA.x * eA + axisB.x * eB + axisC.x * eC, c.y - origin.y - axisA.y * eA + axisB.y * eB + axisC.y * eC, c.z - origin.z - axisA.z * eA + axisB.z * eB + axisC.z * eC],
    [c.x - origin.x + axisA.x * eA + axisB.x * eB + axisC.x * eC, c.y - origin.y + axisA.y * eA + axisB.y * eB + axisC.y * eC, c.z - origin.z + axisA.z * eA + axisB.z * eB + axisC.z * eC],
  ];

  for (const [startIndex, endIndex] of BOX_EDGE_INDEX_PAIRS) {
    positions.push(...corners[startIndex], ...corners[endIndex]);
  }
}

export function createBoundingBoxMeshes(
  boundingBoxViewer3dItems: IBoundingboxViewer3dItem[],
  origin = new THREE.Vector3(),
): THREE.Object3D[] {
  const renderObbs: RenderObb[] = boundingBoxViewer3dItems.flatMap(
    ({ item, color, opacity, outline }) => {
      const seen = new Set<string>();
      return (item.geometry?.Obb3DWorldMmList ?? [])
        .filter((obb) => {
          const key = getDeduplicationKey(obb);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((obb) => ({ item, obb, color, opacity, outline, depthOffsetUnits: getDepthOffsetUnits(item, obb) }));
    },
  );

  const clipPlane: THREE.Plane | null = null;
  const renderObjects: THREE.Object3D[] = [];
  const meshGroups = new Map<string, RenderObb[]>();
  const outlineGroups = new Map<string, { outline: NonNullable<IBoundingboxViewer3dItem["outline"]>; obbs: RoomSectionObb[] }>();

  for (const renderObb of renderObbs) {
    const key = getMeshGroupKey(renderObb);
    const group = meshGroups.get(key);
    if (group) {
      group.push(renderObb);
    } else {
      meshGroups.set(key, [renderObb]);
    }

    if (renderObb.outline) {
      const outlineKey = getOutlineGroupKey(renderObb.outline, renderObb.obb);
      const outlineGroup = outlineGroups.get(outlineKey);
      if (outlineGroup) {
        outlineGroup.obbs.push(renderObb.obb);
      } else {
        outlineGroups.set(outlineKey, { outline: renderObb.outline, obbs: [renderObb.obb] });
      }
    }
  }

  const instanceMatrix = new THREE.Matrix4();
  for (const group of meshGroups.values()) {
    const { color, opacity, depthOffsetUnits } = group[0];
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.MeshLambertMaterial({
      color: new THREE.Color(color),
      opacity,
      transparent: opacity < 1,
      depthWrite: opacity >= 1,
      clippingPlanes: clipPlane ? [clipPlane] : [],
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: depthOffsetUnits,
    });
    const mesh = new THREE.InstancedMesh(geometry, material, group.length);
    mesh.userData.items = group.map(({ item }) => item);
    mesh.renderOrder = 2;

    group.forEach(({ obb }, index) => {
      setObbMatrix(obb, instanceMatrix, origin);
      mesh.setMatrixAt(index, instanceMatrix);
    });

    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingBox();
    mesh.computeBoundingSphere();
    renderObjects.push(mesh);
  }

  for (const { outline, obbs } of outlineGroups.values()) {
    const positions: number[] = [];
    for (const obb of obbs) {
      addObbEdgePositions(obb, positions, origin);
    }
    const edges = new THREE.BufferGeometry();
    edges.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    edges.computeBoundingBox();
    edges.computeBoundingSphere();
    const onTop = outline.renderOnTop === true;
    const lineMaterial = new THREE.LineBasicMaterial({
      color: new THREE.Color(outline.color),
      opacity: outline.opacity ?? 1,
      transparent: (outline.opacity ?? 1) < 1,
      depthTest: !onTop,
      depthWrite: !onTop,
      clippingPlanes: clipPlane ? [clipPlane] : [],
    });

    const lineSegments = new THREE.LineSegments(edges, lineMaterial);
    lineSegments.renderOrder = onTop ? 5 : 2;
    renderObjects.push(lineSegments);
  }

  return renderObjects;
}
